import { observer } from "mobx-react-lite";
import React, { useContext, useCallback } from "react";
import { rootStore } from "../../stores/main";
import { Button, Tooltip } from "antd";
import "./VidContainer.scss";
import { SimpleLoader } from "../SimpleLoader";
import { ResetSceneButton } from "./ResetSceneButton";
import { LayerConnectorMenu } from "./LayerConnectorMenu";
import { setLayerInspectorSelections } from "../../helpers/layerInspectorTools";
import { LayerVisualPropertiesPanel } from "./LayerVisualPropertiesPanel";
import { LayerConnectorMenuButton } from "./LayerConnectorMenuButton";
import type { PlaygroundAddLayerKind } from "../../helpers/layerCreation";
import {
  ImageLayerIcon,
  LayerTypeIcon,
  SolidLayerIcon,
  TextLayerIcon,
} from "../../icons-and-animations/Icons";

const MSTContext = React.createContext(rootStore);
const LAYER_ADD_ACTIONS: Array<{
  kind: PlaygroundAddLayerKind;
  label: string;
  icon: React.ReactNode;
}> = [
  { kind: "text", label: "Add text layer", icon: <TextLayerIcon /> },
  {
    kind: "button",
    label: "Add button layer",
    icon: (
      <span
        aria-hidden="true"
        className="vid-layer-create-toolbar__button-icon"
      />
    ),
  },
  {
    kind: "square",
    label: "Add square layer",
    icon: (
      <span
        aria-hidden="true"
        className="vid-layer-create-toolbar__shape-icon vid-layer-create-toolbar__shape-icon--square"
      />
    ),
  },
  {
    kind: "circle",
    label: "Add circle layer",
    icon: (
      <span
        aria-hidden="true"
        className="vid-layer-create-toolbar__shape-icon vid-layer-create-toolbar__shape-icon--circle"
      />
    ),
  },
  { kind: "image", label: "Add image layer", icon: <ImageLayerIcon /> },
  {
    kind: "video",
    label: "Add video layer",
    icon: <LayerTypeIcon layerType={9} />,
  },
  {
    kind: "audio",
    label: "Add audio layer",
    icon: <LayerTypeIcon layerType={6} />,
  },
  {
    kind: "background",
    label: "Add background layer",
    icon: <SolidLayerIcon />,
  },
];

export const VidContainer = observer(() => {
  const {
    layoutStore: { schemaDrawerOpen },
    platformStore: {
      isLoadingProjectList,
      isLoadingSelectedProject,
      projectWorkspaceVersion,
      selectedProjectId,
      selectedVideoPart,
    },
    playerStore: {
      videoBgColor,
      jsonFile,
      layerInspectorEnabled,
      layerInspectorMode,
      layerInspectorAvailable,
      setLayerInspectorEnabled,
      addLayerToCurrentFrame,
      resetScenePlayer,
      playerLoadError,
    },
  } = useContext(MSTContext);
  const handleVideoPointerDownCapture = useCallback(() => {
    if (
      layerInspectorEnabled &&
      layerInspectorMode === "design" &&
      layerInspectorAvailable
    ) {
      setLayerInspectorEnabled(true);
    }
  }, [
    layerInspectorAvailable,
    layerInspectorEnabled,
    layerInspectorMode,
    setLayerInspectorEnabled,
  ]);
  const handleVideoBackgroundPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!layerInspectorEnabled || layerInspectorMode !== "design") return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[class*="bl_edit_selector"]')) return;
      if (target.closest(".layer-inspector-outline-box")) return;
      if (target.closest("[data-layer-inspector-keep-selection]")) return;
      setLayerInspectorSelections([]);
    },
    [layerInspectorEnabled, layerInspectorMode]
  );
  const handleResetScene = useCallback(() => {
    resetScenePlayer();
  }, [resetScenePlayer]);
  const isProjectLoading =
    isLoadingSelectedProject ||
    (isLoadingProjectList && !selectedVideoPart) ||
    (!!selectedProjectId && !projectWorkspaceVersion);
  const canAddLayer = Boolean(jsonFile);

  return !selectedVideoPart ? (
    <div
      style={{
        width: "100%",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        fontSize: "1rem",
      }}
    >
      {isProjectLoading
        ? "Loading scene..."
        : "Render a composition from the Extension to start"}
    </div>
  ) : (
    <div
      className={`VidContainerWrapper${
        schemaDrawerOpen ? " VidContainerWrapper--schema-open" : ""
      }`}
      hidden={!jsonFile}
    >
      {layerInspectorMode === "design" && (
        <div
          className="vid-layer-create-toolbar"
          data-layer-inspector-keep-selection
        >
          {LAYER_ADD_ACTIONS.map((action) => (
            <Tooltip key={action.kind} title={action.label} placement="right">
              <Button
                className="vid-layer-create-toolbar__button"
                disabled={!canAddLayer}
                icon={action.icon}
                aria-label={action.label}
                onClick={() => addLayerToCurrentFrame(action.kind)}
              />
            </Tooltip>
          ))}
        </div>
      )}
      <div className="VidContainerWrapper-inner-container">
        <div
          className="VidContainerWrapper-inner-container2"
          style={{ position: "relative" }}
        >
          <div
            id="vid-container"
            className="vid-container"
            style={{
              backgroundImage: videoBgColor,
              backgroundSize: "60px 60px",
              backgroundPosition: "0 0, 30px 30px",
            }}
            onPointerDownCapture={handleVideoPointerDownCapture}
            onPointerDown={handleVideoBackgroundPointerDown}
          />
          <ResetSceneButton
            sceneName={selectedVideoPart?.name}
            resetPlayerCallback={handleResetScene}
          />
          <LayerConnectorMenuButton />
        </div>
      </div>
      {layerInspectorMode === "design" && <LayerVisualPropertiesPanel />}
      {playerLoadError && (
        <div className="scene-load-error" role="alert">
          <p>{playerLoadError}</p>
          <Button onClick={handleResetScene}>Retry scene</Button>
        </div>
      )}
      <div id="loader-container">
        <div id="loader-inner-container">
          <SimpleLoader />
        </div>
      </div>

      <LayerConnectorMenu />
    </div>
  );
});
